import { combineEpics, ofType } from 'redux-observable'
import { of } from 'rxjs'
import { catchError, map, mergeMap, takeUntil } from 'rxjs/operators'

import {
  activateConfigError,
  activateConfigSuccess,
  fetchDataError,
  fetchDataSuccess,
  fetchLicensedDomainsError,
  fetchLicensedDomainsSuccess,
  updateConfigError,
  updateConfigSuccess,
  validateConnectionError,
  validateConnectionSuccess,
} from './actions'
import {
  ACTIVATE_CONFIG_ASYNC,
  DATA_REQUEST_ASYNC,
  FETCH_CONFIG_ASYNC,
  FETCH_LICENSED_DOMAINS_ASYNC,
  UPDATE_CONFIG_ASYNC,
  VALIDATE_CONNECTION_ASYNC,
} from './constants'

const getErrorStatus = (error) => error?.status || error?.response?.status

const getCriticalErrorType = (error) => {
  const status = getErrorStatus(error)
  if (![403, 404, 500].includes(status)) return null
  const detail = (error?.response?.data?.detail || error?.detail || '').toString().toLowerCase()
  if (status === 403) return detail.includes('addon inactive') ? 'addon_inactive' : 'no_permissions'
  if (status === 404) return 'wrong_integration'
  if (status === 500) return 'broken'
  return null
}

// Step 1 -> 2
const createConfigEpic = (action$, state$, { api, epicsErrorHandler }) =>
  action$.pipe(
    ofType(DATA_REQUEST_ASYNC.INITIATE),
    mergeMap(() => {
      const companyId = state$.value.partnerOptions?.selectedCompany?.value || ''

      return api.settings.getEmailEncryptionConfig(companyId).pipe(
        map((existing) => fetchDataSuccess(existing)),
        catchError(() =>
          api.settings.createEmailEncryptionConfig(companyId).pipe(
            map((response) => fetchDataSuccess(response)),
            catchError((error) =>
              of({
                ...epicsErrorHandler(fetchDataError, error),
                errorType: getCriticalErrorType(error),
              })
            )
          )
        )
      )
    })
  )
// Read-only on mount: unlike createConfigEpic it never creates a tenant. The proxy answers 404 only
// when the company has no OES config yet, so that loads as an empty config; any other failure is real.
const fetchConfigEpic = (action$, state$, { api, epicsErrorHandler }) =>
  action$.pipe(
    ofType(FETCH_CONFIG_ASYNC.INITIATE),
    mergeMap(() => {
      const companyId = state$.value.partnerOptions?.selectedCompany?.value || ''

      return api.settings.getEmailEncryptionConfig(companyId).pipe(
        map((config) => FETCH_CONFIG_ASYNC.onSuccess(config)),
        catchError((error) =>
          getErrorStatus(error) === 404
            ? of(FETCH_CONFIG_ASYNC.onSuccess({}))
            : of({
                ...epicsErrorHandler(FETCH_CONFIG_ASYNC.onError, error),
                errorType: getCriticalErrorType(error),
              })
        ),
        takeUntil(action$.pipe(ofType(FETCH_CONFIG_ASYNC.CANCEL)))
      )
    })
  )

// Step 3
const validateEpic = (action$, state$, { api, epicsErrorHandler }) =>
  action$.pipe(
    ofType(VALIDATE_CONNECTION_ASYNC.INITIATE),
    mergeMap(() => {
      const companyId = state$.value.partnerOptions?.selectedCompany?.value || ''

      return api.settings.getEmailEncryptionConnectionStatus(companyId).pipe(
        map((response) => validateConnectionSuccess(response)),
        catchError((error) =>
          of({
            ...epicsErrorHandler(validateConnectionError, error),
            errorType: getCriticalErrorType(error),
          })
        )
      )
    })
  )

// Step 4
const activateEpic = (action$, state$, { api, epicsErrorHandler }) =>
  action$.pipe(
    ofType(ACTIVATE_CONFIG_ASYNC.INITIATE),
    mergeMap(() => {
      const companyId = state$.value.partnerOptions?.selectedCompany?.value || ''

      return api.settings.activateEmailEncryptionConfig(companyId).pipe(
        map((response) => activateConfigSuccess(response)),
        catchError((error) =>
          of({
            ...epicsErrorHandler(activateConfigError, error),
            errorType: getCriticalErrorType(error),
          })
        )
      )
    })
  )

const updateConfigEpic = (action$, state$, { api, epicsErrorHandler }) =>
  action$.pipe(
    ofType(UPDATE_CONFIG_ASYNC.INITIATE),
    mergeMap(({ payload = {} }) => {
      const companyId = state$.value.partnerOptions?.selectedCompany?.value || ''

      return api.settings.updateEmailEncryptionConfig(companyId, payload).pipe(
        map((response) => updateConfigSuccess(response)),
        catchError((error) =>
          of({
            ...epicsErrorHandler(updateConfigError, error),
            errorType: getCriticalErrorType(error),
          })
        )
      )
    })
  )

const fetchLicensedDomainsEpic = (action$, state$, { api, epicsErrorHandler }) =>
  action$.pipe(
    ofType(FETCH_LICENSED_DOMAINS_ASYNC.INITIATE),
    mergeMap(() => {
      const companyId = state$.value.partnerOptions?.selectedCompany?.value || ''
      return api.settings.getGeneralSettings(companyId).pipe(
        map((response) => {
          const rawDomains = (response?.allowedDomains || '')
            .split(',')
            .map((d) => d.trim())
            .filter(Boolean)
          const domains = rawDomains.map((domain) => ({
            domain,
            locked: domain.includes('.onmicrosoft.com'),
          }))
          return fetchLicensedDomainsSuccess(domains)
        }),
        catchError((error) => of(epicsErrorHandler(fetchLicensedDomainsError, error)))
      )
    })
  )

export default combineEpics(
  createConfigEpic,
  fetchConfigEpic,
  validateEpic,
  activateEpic,
  updateConfigEpic,
  fetchLicensedDomainsEpic
)
